import React, { useState, useEffect, useRef, useCallback } from "react";
import Grid from "@mui/material/Grid2";

import { 
  Campaign, 
  Encounter,
} from "@dndModels";
import API from "@utilities/smart_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";

import CharConnectUtilities from "@dndUtilities/char_connect_utilities";
import LoadingBox from "@components/input/LoadingBox";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import EncounterParent from "@dndComponents/campaign/encounters/EncounterParent";
import EncounterCard from "./EncounterCard";
import AddButton from "@components/input/buttons/icon_buttons/AddButton";
import FlexBox from "@components/layout/FlexBox";
import { addCharacterParticipants } from "@dndUtilities/encounter_participants";
import RefreshButton from "@components/input/buttons/icon_buttons/RefreshButton";
import ViewArchivesCheckBox from "@dndComponents/campaign/ViewArchivesCheckBox";

const api = API.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
const connect_util = CharConnectUtilities.getInstance();

interface Props {
  campaign: Campaign;
}

export default function CampaignEncountersInput({ campaign }: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [selected, setSelected] = useState<Encounter | undefined>(undefined);
  const [view_archives, setViewArchives] = useState(false);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback( async () => {
    // debugger;
    if (campaign.encounters.length === 0) {
      campaign.encounters = await api.getCampaignObjects<Encounter>({ data_type: "encounter", campaign });
    }
    api.queuePriorityLoad(["creature","creature_feature"]);
    setLoading(false);
  }, [campaign]);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      setLoading(true);
      setTimeout(() => {
        load();
      }, 100);
    }
  }, [fetched, load]);
    
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const renderExtras = () => {
    return (
      <ModalOrDrawer 
        open={!!selected} 
        onClose={() => { setSelected(undefined); }}
        full_page>
        <EncounterParent
          id={ selected ? selected._id : "" }
          encounter={selected}
        />
      </ModalOrDrawer>
    )
  }

  const renderEncounters = () => {
    const shown_encounters = view_archives ? campaign.archived_encounters : campaign.encounters;
    return (
      <FlexBox>
        <Grid container spacing={0} direction="row">
          { [...shown_encounters].sort((a, b) => a.name.localeCompare(b.name)).map(encounter => {
            return (
              <Grid size={{ xs:12, sm: 6, md: 4, lg: 3 }} key={encounter._id}>
                <EncounterCard
                  encounter={encounter}
                  disabled={processing}
                  onSelect={() => { setSelected(encounter); }}
                  onDelete={ async () => {
                    setProcessing(true);
                    await api.deleteObject("encounter", encounter);
                    campaign.encounters = campaign.encounters.filter(o => o._id !== encounter._id);
                    campaign.archived_encounters = campaign.archived_encounters.filter(o => o._id !== encounter._id);
                    setProcessing(false);
                  }}
                  onArchive={ async () => {
                    setProcessing(true);
                    await api.setCampaignObjectArchived({ campaign, data_type: "encounter", obj: encounter, is_archived: !encounter.is_archived });
                    setProcessing(false);
                  }}
                />
              </Grid>
            );
          })}
        </Grid>
      </FlexBox>
    );
  }

  if (!fetched || loading) {
    return (<LoadingBox />);
  } else {
    return (
      <FlexBox direction="column">
        <FlexBox direction="row">
          <FlexBox xs_expand={0} className="padded">
            <AddButton
              disabled={processing}
              onClick={ async () => {
                if (api.user) {
                  setProcessing(true);
                  const new_encounter = new Encounter();
                  new_encounter.campaign_id = campaign._id;
                  new_encounter.campaign = campaign;
                  addCharacterParticipants(new_encounter, campaign.active_characters, "white");
                  await api.upsertObject("encounter", new_encounter);
                  campaign.encounters.push(new_encounter);
                  setSelected(new_encounter);
                  setProcessing(false);
                }
              }}
            />
          </FlexBox>
          <FlexBox xs_expand={0} className="padded">
            <RefreshButton
              disabled={processing}
              onClick={ async () => {
                setProcessing(true);
                if (view_archives) {
                  await api.loadArchivedCampaignObjects({ campaign, data_type: "encounter", refresh: true });
                } else {
                  campaign.encounters = [];
                  campaign.encounters = await api.getCampaignObjects<Encounter>({ data_type: "encounter", campaign });
                }
                setProcessing(false);
              }}
            />
          </FlexBox>
          <FlexBox xs_expand={0} className="padded">
            <ViewArchivesCheckBox
              campaign={campaign}
              data_type="encounter"
              value={view_archives}
              disabled={processing}
              onChange={setViewArchives}
            />
          </FlexBox>
        </FlexBox>
        { renderEncounters() }
        { renderExtras() }
      </FlexBox>
    );
  } 
}
